import makeStyles from '@mui/styles/makeStyles';
import { Dialog, DialogContent, DialogTitle, DialogActions, TextField, Button } from '@mui/material';
import { API } from '../../../shared/constants/constants';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import { useState } from 'react';
import { getOrganizationsByCaseInsensitiveName, getOrgsByCaseInsensitiveName } from '../contactsRequest';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import {
  normalizeOrganizationPayload,
  normalizeOrganizationQueryName,
} from '../../../shared/requests/organizationNormalization';

const ContactsPageAddOutletModal = (props) => {
  const { openModal, setOpenModal, setRefreshContacts, category } = props;
  const [textFieldValue, setTextFieldValue] = useState('');

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const useStyles = makeStyles(() => ({
    dialog: {
      minHeight: '15vh',
      overflow: 'visible !important',
    },
  }));
  const classes = useStyles();

  const duplicatesQuery = useQuery({
    queryKey: ['orgDuplicates', category, textFieldValue],
    queryFn: () => {
      if (category === 'Outlets') {
        return getOrgsByCaseInsensitiveName(textFieldValue);
      }
      return getOrganizationsByCaseInsensitiveName(normalizeOrganizationQueryName(textFieldValue));
    },
    enabled: false,
    staleTime: 0,
  });

  const createOrgMutation = useMutation({
    mutationFn: (newOrg) => {
      if (category === 'Outlets') {
        const normalizedOrg = { name: newOrg?.name };
        return API.post('/org', normalizedOrg);
      }
      return API.post('/organization', normalizeOrganizationPayload(newOrg));
    },
    onSuccess: async (response) => {
      if (response.status !== 201) {
        alert(response.statusText);
        return;
      }
      dispatch(
        openSnackbar({
          severity: 'success',
          message: `${category.slice(0, -1)}: ${textFieldValue} created!`,
        })
      );
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      setRefreshContacts((prev) => !prev);
      handleClose();
    },
    onError: (err) => {
      alert(`post failed - ${err}`);
    },
  });

  const handleClose = () => {
    setOpenModal(false);
  };

  const countDuplicates = async () => {
    try {
      const { data } = await duplicatesQuery.refetch();
      const responseData = Array.isArray(data?.data) ? data.data : [];
      return responseData.length;
    } catch (error) {
      alert(`get failed - ${error}`);
      return 0;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const duplicates = await countDuplicates();

    if (duplicates > 0) {
      dispatch(
        openSnackbar({
          severity: 'warning',
          message: `${duplicates} ${category
            .toLowerCase()
            .slice(0, -1)}(s) with the same name already exists. Please check if you are trying to add an ${category
            .toLowerCase()
            .slice(0, -1)} that already exists.`,
        })
      );
      return;
    } else {
      await createOrgMutation.mutateAsync({ name: textFieldValue });
      event.stopPropagation();
    }
  };

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.dialog}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>New {category.slice(0, -1)}</DialogTitle>
        <DialogContent className={classes.dialog}>
          <TextField
            required
            autoFocus
            value={textFieldValue}
            onChange={(event) => {
              setTextFieldValue(event.target.value);
            }}
            margin="dense"
            id="name"
            label="Name"
            type="text"
            variant="standard"
            data-cy="name-input-ContactsPageAddOutletModal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-cy="cancel-button-ContactsPageAddOutletModal">
            Cancel
          </Button>
          <Button type="submit" data-cy="add-button-ContactsPageAddOutletModal">
            Add
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContactsPageAddOutletModal;
